import { answerOf, countdownAhead, normalizeLeads, place, reasonText, repeatText, whenText } from '../../lib/calendar.js'
import { pushOn } from '../../lib/push.js'
import { routeHref } from '../../lib/router.js'
import { relativeTime } from '../../lib/format.js'
import { saveText } from '../../lib/mail.js'

const Fez = window.Fez;
if (!Fez) throw new Error('fez runtime not loaded: load fez (window.Fez) before component modules');

// --- calendar-event-dialog ---
let __fez_component_0;
{
const OUTCOMES = { added: 'Added it', updated: 'Changed it', current: 'Same version', older: 'Older version', cancelled: 'Cancelled it', replied: 'An answer', waiting: 'Waiting', ignored: 'Ignored' }
// What the messages of an event made here are: the ones we sent, and the answers.
const SENT = { added: 'Invitation', updated: 'Update', cancelled: 'Cancellation', replied: 'Answered' }
__fez_component_0 = class {
  PROPS = {
    event: { type: String, required: true },
    calendar: String,
    occurrence: Object,
  }

  init() {
    this.state.loading = true
    this.state.error = ''
    this.state.view = null
    this.state.busy = ''
    this.state.ask = ''
    this.state.reminding = false
    this.state.reminderError = ''
    this.state.pushOff = false
    this.state.counting = false
  }

  onMount() {
    this.load()
    if (this.globalState.me && !this.globalState.sudo) pushOn().then((on) => { this.state.pushOff = !on })
  }

  // The holder this dialog acts for: the one it was opened from, else the one the API chose.
  holder() {
    return this.props.calendar || this.state.view?.calendar || undefined
  }

  async remind(list) {
    const before = this.state.view
    this.state.view = { ...before, reminders: list }
    this.state.reminding = true
    try {
      // Reminders are mine: only they change, the view stays the opened calendar's.
      const view = await window.App.api('calendar/event_reminders', { event: this.props.event, calendar: this.holder(), reminders: list }, { silent: true })
      this.state.view = { ...this.state.view, reminders: view.reminders }
      this.state.reminderError = ''
    } catch (error) {
      this.state.view = before
      this.state.reminderError = error.details?.reminders || error.message
    } finally {
      this.state.reminding = false
    }
  }

  async countdown(on) {
    this.state.counting = true
    try {
      this.state.view = await window.App.api('calendar/event_countdown', { event: this.props.event, calendar: this.holder(), countdown: on }, { silent: true })
    } catch (error) {
      window.Toast.error(error.message)
    } finally {
      this.state.counting = false
    }
  }

  async load() {
    try {
      this.state.view = await window.App.api('calendar/event', { event: this.props.event, calendar: this.holder() }, { silent: true })
      // A link without the instance opens as "Event": name it once the event is known.
      if (!this.props.occurrence?.start) window.Dialog.setTitle(this.state.view.summary || '(no title)')
      this.state.error = ''
    } catch (error) {
      this.state.error = error.code === 'not_found' ? 'This event is gone from the calendar.' : error.message
    } finally {
      this.state.loading = false
    }
  }

  async settle(change, apply) {
    this.state.busy = change
    try {
      const action = apply ? 'event_change_apply' : 'event_change_ignore'
      await window.App.api(`calendar/${action}`, { change })
      await this.load()
    } finally {
      this.state.busy = ''
    }
  }

  answered(view) {
    this.state.view = view
  }

  labeled(view) {
    this.state.view = view
  }

  // Edit and Delete of one instance of a series ask which is meant first.
  edit() {
    if (this.state.series) this.state.ask = 'edit'
    else window.Dialog.close({ edit: 'all', view: this.state.view })
  }

  async remove() {
    if (this.state.oneOrAll) {
      this.state.ask = 'delete'
      return
    }
    const view = this.state.view
    const name = this.state.calendarName
    const text = this.state.maker
      ? view.attendees.length ? 'It leaves the calendar. Outside attendees get a cancellation, and our own mailboxes it invites keep it struck through.' : 'It leaves the calendar.'
      : view.origin === 'own' ? `It leaves ${name}; the organizer is not told.` : `It leaves ${name}; the organizer is not told. A later update of the invite adds it again.`
    const title = this.state.maker || !view.shared ? 'Delete this event?' : `Remove it from ${name}?`
    const ok = await window.Dialog.confirm({ title, text, confirm: this.state.deleteLabel, danger: true })
    if (!ok) return
    await window.App.api('calendar/event_delete', { event: this.props.event, calendar: this.holder() })
    window.Dialog.close(true)
  }

  async choose(scope) {
    const ask = this.state.ask
    this.state.ask = ''
    if (ask === 'edit') {
      window.Dialog.close({ edit: scope, view: this.state.view })
      return
    }
    this.state.busy = 'delete'
    try {
      await window.App.api('calendar/event_delete', { event: this.props.event, calendar: this.holder(), instance: scope === 'one' ? this.props.occurrence.instance : undefined })
      window.Dialog.close(true)
    } finally {
      this.state.busy = ''
    }
  }

  unask() {
    this.state.ask = ''
  }

  download() {
    const view = this.state.view
    saveText(view.ics, `${(view.summary || 'event').replace(/[^\w .-]+/g, '').trim() || 'event'}.ics`, 'text/calendar')
  }

  beforeRender() {
    const base = this.state.view
    if (!base) return
    const occurrence = this.props.occurrence
    // One instance changed on its own says its own title, place and text.
    const changed = base.recurring && occurrence ? (base.overrides || []).find((entry) => entry.instance === occurrence.instance) : null
    const view = changed ? { ...base, summary: changed.summary, location: changed.location, url: changed.url, description: changed.description } : base
    this.state.shown = view
    this.state.editable = Boolean(view.editable)
    this.state.feed = view.origin === 'feed'
    this.state.reminders = normalizeLeads(view.reminders)
    const calendars = view.calendars || []
    const opened = calendars.find((entry) => entry.calendar === (this.props.calendar || view.calendar)) || calendars[0]
    this.state.calendarName = opened?.name || 'this calendar'
    // The maker deletes the event itself; anyone else takes it out of the opened calendar.
    this.state.maker = Boolean(view.editable && view.owner && view.calendar === view.owner)
    this.state.series = Boolean(view.recurring && occurrence?.instance !== undefined && occurrence?.instance !== null)
    // One instance can go only for the maker, or from a row no other calendar holds.
    this.state.oneOrAll = this.state.series && (this.state.maker || !view.shared)
    this.state.deleteLabel = this.state.maker || !view.shared ? 'Delete' : `Remove from ${this.state.calendarName}`
    this.state.askText = this.state.ask === 'edit' ? 'Change only this event, or every event in the series?' : 'Delete only this event, or every event in the series?'
    // The instance clicked of a series; the event's own times otherwise, so an applied change shows.
    const when = view.recurring && occurrence?.start ? occurrence : { start: view.start, end: view.end, all_day: view.all_day, floating: view.floating, tzid: view.tzid }
    const placed = when.start ? place({ ...when, tzid: view.tzid }) : null
    this.state.when = placed ? whenText(placed) : ''
    this.state.ahead = view.countdown && placed && view.status !== 'cancelled' ? countdownAhead(placed.firstDay) : ''
    this.state.repeat = repeatText(view.rrule)
    // An online meeting's "location" is no place to look up.
    const online = /https?:\/\/|microsoft teams|zoom|google meet|webex/i.test(view.location || '')
    this.state.mapHref = view.location && !online ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(view.location)}` : ''
    this.state.badges = [
      view.status === 'cancelled' && { label: 'Cancelled', tone: 'danger' },
      view.status === 'tentative' && { label: 'Tentative', tone: 'neutral' },
      view.changes.length && { label: 'Update waiting', tone: 'warning' },
      view.status !== 'cancelled' && answerOf(view.my_partstat),
    ].filter(Boolean)
    this.state.people = view.attendees.map((person) => ({
      ...person,
      label: person.name || person.address,
      me: person.address === view.my_address,
      organizer: person.address === view.organizer,
      answer: answerOf(person.partstat),
    }))
    this.state.organizerLabel = view.organizer ? (view.organizer_name ? `${view.organizer_name} <${view.organizer}>` : view.organizer) : ''
    this.state.changes = view.changes.map((change) => {
      const target = change.card.start ? place(change.card) : null
      const says = change.method === 'CANCEL' ? 'Cancels it' : change.method === 'REPLY' ? `${change.card.reply?.name || change.card.reply?.address || 'Someone'}: ${answerOf(change.card.reply?.partstat)?.label || change.card.reply?.partstat || ''}` : target ? `Moves it to ${whenText(target)}` : 'Changes it'
      return { ...change, says, why: reasonText(change.reason) }
    })
    const holder = opened
    // A message is in one of my mailboxes; the view's own mailbox when it does not say which.
    const inbox = (mail) => routeHref('inbox', { container: mail.container || holder?.container, mailbox: mail.mailbox || (holder?.kind === 'mailbox' ? holder.calendar : ''), thread: mail.thread })
    this.state.mails = view.mails.map((mail) => ({
      ...mail,
      // An answer from one of our own mailboxes came without mail: no message to open.
      href: mail.thread ? inbox(mail) : '',
      key: mail.email || `${mail.from}:${mail.received_at}`,
      title: mail.email ? mail.subject || '(no subject)' : `${mail.from} answered`,
      outcome: (view.origin === 'own' ? SENT[mail.outcome] : OUTCOMES[mail.outcome]) || OUTCOMES[mail.outcome] || mail.outcome,
      age: relativeTime(mail.received_at),
    }))
    this.state.emailHref = view.email ? inbox(view.email) : ''
    this.state.where = [calendars.length && `In ${calendars.map((entry) => entry.name).join(', ')}`, view.source_name && `through ${view.source_name}`, view.subscription_name && `from the subscribed calendar ${view.subscription_name}, which changes it`].filter(Boolean).join(', ')
  }

  CSS = `:fez {
display:block;.ev-badges{display:flex;flex-wrap:wrap;gap:var(--space-2);margin-bottom:var(--space-3)}.ev-row{display:flex;gap:var(--space-3);padding:var(--space-2) 0}.ev-icon{display:flex;flex:none;padding-top:2px;color:var(--color-text-subtle)}.ev-text{display:grid;flex:1;gap:2px;min-width:0;overflow-wrap:anywhere}.ev-sub{color:var(--color-text-muted);font-size:var(--text-sm)}.ev-link{justify-self:start;font-size:var(--text-sm)}.ev-warn{color:var(--color-warning);font-size:var(--text-sm)}.ev-check{display:flex;flex-wrap:wrap;align-items:center;gap:var(--space-2) var(--space-3);font-size:var(--text-sm);label{display:inline-flex;align-items:center;gap:var(--space-2);font-weight:var(--weight-medium);cursor:pointer}}.ev-ahead{color:var(--color-accent);font-weight:var(--weight-semibold)}.ev-people{display:grid;gap:var(--space-1)}.ev-person{display:flex;align-items:center;gap:var(--space-2);min-width:0;font-size:var(--text-sm)}.ev-person-name{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.ev-description{max-height:220px;overflow-y:auto;padding:var(--space-3);border-radius:var(--radius-md);background:var(--color-bg-subtle);font-size:var(--text-sm);white-space:pre-wrap;overflow-wrap:anywhere}.ev-section{margin-top:var(--space-4)}.ev-section h3{margin:0 0 var(--space-2);color:var(--color-text-subtle);font-size:var(--text-xs);font-weight:var(--weight-bold);letter-spacing:0.06em;text-transform:uppercase}.ev-change{display:grid;gap:var(--space-2);padding:var(--space-3);border:1px solid var(--color-warning);border-radius:var(--radius-md);background:var(--color-warning-soft);font-size:var(--text-sm)}.ev-change + .ev-change{margin-top:var(--space-2)}.ev-change-says{font-weight:var(--weight-semibold)}.ev-change-actions{display:flex;gap:var(--space-2)}.ev-mail{display:flex;align-items:baseline;gap:var(--space-2);padding:var(--space-1) 0;font-size:var(--text-sm)}.ev-mail-subject{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.ev-mail-meta{flex:none;color:var(--color-text-muted)}.ev-foot{margin-top:var(--space-4);color:var(--color-text-muted);font-size:var(--text-xs)}.ev-spacer{flex:1}.ev-ask{display:grid;gap:var(--space-3);margin-top:var(--space-6);padding:var(--space-3) var(--space-4);border:1px solid var(--color-border);border-radius:var(--radius-md);background:var(--color-bg-subtle)}.ev-ask-text{font-weight:var(--weight-semibold)}.ev-ask-actions{display:flex;flex-wrap:wrap;gap:var(--space-2)}.ev-note{display:grid;place-items:center;min-height:160px}
}`

  HTML = `{#if state.error}
<div class="ev-note"><ui-empty icon="calendar-blank" title="Event not found" text={state.error}></ui-empty></div>
{:else if state.loading}
<div class="ev-note"><ui-spinner></ui-spinner></div>
{:else}
{#if state.badges.length}
<div class="ev-badges">
{#each state.badges as badge}
<ui-badge tone={badge.tone} label={badge.label}></ui-badge>
{/each}
</div>
{/if}
{#if state.when}
<div class="ev-row">
<span class="ev-icon"><ui-icon name="clock" size="18"></ui-icon></span>
<div class="ev-text">
<span>{state.when}</span>
{#if state.repeat}<span class="ev-sub">{state.repeat}</span>{/if}
{#if state.view.zone_guessed}<span class="ev-warn">The invite's time zone is unknown: the time is shown as written.</span>{/if}
</div>
</div>
{/if}
{#if state.shown.location}
<div class="ev-row">
<span class="ev-icon"><ui-icon name="map-pin" size="18"></ui-icon></span>
<div class="ev-text">
<span>{state.shown.location}</span>
{#if state.mapHref}<a class="ev-link" href={state.mapHref} target="_blank" rel="noreferrer noopener">Show on a map</a>{/if}
</div>
</div>
{/if}
{#if state.shown.url}
<div class="ev-row">
<span class="ev-icon"><ui-icon name="video-camera" size="18"></ui-icon></span>
<div class="ev-text">
<span><ui-button size="sm" variant="primary" icon="arrow-square-out" label="Join" href={state.shown.url} target="_blank" rel="noreferrer noopener"></ui-button></span>
<span class="ev-sub">{state.shown.url}</span>
</div>
</div>
{/if}
{#if state.organizerLabel || state.people.length}
<div class="ev-row">
<span class="ev-icon"><ui-icon name="users" size="18"></ui-icon></span>
<div class="ev-text">
{#if state.organizerLabel}<span class="ev-sub">Organized by {state.organizerLabel}</span>{/if}
<div class="ev-people">
{#each state.people as person}
<div class="ev-person" key={person.address} title={person.address}>
<ui-avatar name={person.label} size="sm"></ui-avatar>
<span class="ev-person-name">{person.label}{person.me ? ' (this calendar)' : ''}</span>
{#if person.answer}<ui-badge tone={person.answer.tone} label={person.answer.label}></ui-badge>{/if}
</div>
{/each}
</div>
</div>
</div>
{/if}
{#if state.view.answerable}
<div class="ev-row">
<span class="ev-icon"><ui-icon name="envelope-simple" size="18"></ui-icon></span>
<div class="ev-text">
<calendar-answer event={state.view.ref} calendar={state.view.calendar || ''} partstat={state.view.my_partstat || ''} :on_answered="(view) => fez.answered(view)"></calendar-answer>
</div>
</div>
{/if}
{#if globalState.me}
<div class="ev-row">
<span class="ev-icon"><ui-icon name="bell" size="18"></ui-icon></span>
<div class="ev-text">
<calendar-reminders label="Notify me" :value="state.reminders" error={state.reminderError} :disabled="state.reminding" :on_change="(list) => fez.remind(list)"></calendar-reminders>
{#if state.pushOff && state.reminders.length}
<span class="ev-sub">Reminders show on devices with notifications on. <a href="#/settings" onclick="Dialog.close()">Turn them on in Settings</a> for this device.</span>
{/if}
</div>
</div>
{/if}
<div class="ev-row">
<span class="ev-icon"><ui-icon name="tag" size="18"></ui-icon></span>
<div class="ev-text">
<event-label-picker event={props.event} calendar={state.view.calendar || ''} :labels="state.view.labels || []" :on_change="(view) => fez.labeled(view)"></event-label-picker>
</div>
</div>
<div class="ev-row">
<span class="ev-icon"><ui-icon name="hourglass-simple" size="18"></ui-icon></span>
<div class="ev-text ev-check">
<label><input type="checkbox" checked={state.view.countdown} :disabled="state.counting" onchange="fez.countdown(this.checked)"> Count down to it</label>
{#if state.ahead}<span class="ev-ahead">{state.ahead}</span>{/if}
</div>
</div>
{#if state.emailHref}
<div class="ev-row">
<span class="ev-icon"><ui-icon name="envelope-simple" size="18"></ui-icon></span>
<div class="ev-text">
<span>{state.view.email.subject || '(no subject)'}</span>
<a class="ev-link" href={state.emailHref} onclick="Dialog.close()">Open email</a>
</div>
</div>
{/if}
{#if state.shown.description}
<div class="ev-section"><div class="ev-description">{state.shown.description}</div></div>
{/if}
{#if state.changes.length}
<div class="ev-section">
<h3>Waiting for you</h3>
{#each state.changes as change}
<div class="ev-change" key={change.ref}>
<span class="ev-change-says">{change.says}</span>
<span>{change.why}</span>
<div class="ev-change-actions">
<ui-button size="sm" variant="primary" icon="check" label="Apply" loading={state.busy === change.ref} onclick="fez.settle('{change.ref}', true)"></ui-button>
<ui-button size="sm" label="Ignore" onclick="fez.settle('{change.ref}', false)"></ui-button>
</div>
</div>
{/each}
</div>
{/if}
{#if state.mails.length}
<div class="ev-section">
<h3>Messages</h3>
{#each state.mails as mail}
<div class="ev-mail" key={mail.key}>
{#if mail.href}<a class="ev-mail-subject" href={mail.href} onclick="Dialog.close()">{mail.title}</a>{:else}<span class="ev-mail-subject">{mail.title}</span>{/if}
<span class="ev-mail-meta">{mail.outcome}, {mail.age}</span>
{#if mail.note}<span class="ev-mail-meta">"{mail.note}"</span>{/if}
</div>
{/each}
</div>
{/if}
<p class="ev-foot">
{state.where}{state.where ? '. ' : ''}{state.view.trusted ? '' : 'The organizer of this invite could not be verified.'}
</p>
{#if state.ask}
<div class="ev-ask">
<span class="ev-ask-text">{state.askText}</span>
<div class="ev-ask-actions">
<ui-button label="Cancel" onclick="fez.unask()"></ui-button>
<span class="ev-spacer"></span>
<ui-button variant={state.ask === 'delete' ? 'danger' : 'secondary'} label="This event" :loading="state.busy === 'delete'" onclick="fez.choose('one')"></ui-button>
<ui-button variant={state.ask === 'delete' ? 'danger' : 'primary'} label="All events" onclick="fez.choose('all')"></ui-button>
</div>
</div>
{:else}
<div class="dialog-actions">
{#unless state.feed}<ui-button variant="danger-ghost" icon="trash" label={state.deleteLabel} onclick="fez.remove()"></ui-button>{/unless}
<span class="ev-spacer"></span>
<ui-button icon="download-simple" title="Download .ics" aria-label="Download .ics" onclick="fez.download()"></ui-button>
{#if state.editable}<ui-button icon="pencil-simple" label="Edit" onclick="fez.edit()"></ui-button>{/if}
<ui-button variant="primary" label="Done" onclick="Dialog.close()"></ui-button>
</div>
{/if}
{/if}`
};
}

Fez('calendar-event-dialog', __fez_component_0);

export default __fez_component_0;
